import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getScrapLogs, startScraping } from "../api/balanceApi";
import UploadExcelAcc from "./UploadExcel";
import { useOpenRoute } from "../utils/openRouteUtils";

const { Title, Text } = Typography;

function formatDateTime(value) {
  return value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "-";
}

function getLastLog(record) {
  return (
    record.scrapDate ||
    record.updatedAt ||
    record.lastSyncedAt ||
    record.lastLogAt ||
    record.scrapTime ||
    record.createdAt ||
    null
  );
}

function isWithinRange(record, hours) {
  const time = getLastLog(record);
  if (!time) return false;
  return dayjs().diff(dayjs(time), "hour", true) <= Number(hours);
}

function getBankLabel(record) {
  return record.bankDisplayName || record.bank || "-";
}

function getSelectedBankValue(record) {
  return record?.bank || null;
}

function getInstitutionName(record) {
  return record.institutionName || "Байгууллагагүй";
}

function getInstitutionActive(record) {
  return Boolean(record?.institutionActive);
}

function getInstitutionStatusLabel(record) {
  return getInstitutionActive(record) ? "ИДЭВХТЭЙ" : "ИДЭВХГҮЙ";
}

function getInstitutionStatusColor(record) {
  return getInstitutionActive(record) ? "green" : "default";
}

function getBankAccountActive(record) {
  return Boolean(record?.bankAccountActive);
}

function getAccountStatusLabel(record) {
  return getBankAccountActive(record) ? "ИДЭВХТЭЙ" : "ИДЭВХГҮЙ";
}

function getAccountStatusColor(record) {
  return getBankAccountActive(record) ? "green" : "default";
}

function getBankGroupStatusLabel(record) {
  const institutionActive = Boolean(record?.institutionActive);
  const activeCount = Number(record?.activeAccountCount || 0);
  const totalCount = Number(record?.totalAccounts || 0);

  if (!institutionActive) return "ИДЭВХГҮЙ";
  if (totalCount === 0 || activeCount === 0) return "ИДЭВХГҮЙ";
  if (activeCount === totalCount) return "БҮХ ACCOUNT ИДЭВХТЭЙ";
  return `${activeCount}/${totalCount} ИДЭВХТЭЙ`;
}

function getBankGroupStatusColor(record) {
  const institutionActive = Boolean(record?.institutionActive);
  const activeCount = Number(record?.activeAccountCount || 0);
  const totalCount = Number(record?.totalAccounts || 0);

  if (!institutionActive) return "default";
  if (totalCount === 0 || activeCount === 0) return "default";
  if (activeCount === totalCount) return "green";
  return "gold";
}

function getAccountNumber(record) {
  return record.accountNumber || "-";
}

function getSelectedAccountNumber(record) {
  return record?.accountNumber || null;
}

function getBankAccountId(record) {
  return record.bankAccountId || null;
}

function getCurrency(record) {
  return record.currency || "-";
}

function getRawAccountType(record) {
  return record.type || "UNKNOWN";
}

function getAccountType(record) {
  const scrapType = String(getRawAccountType(record)).toUpperCase();

  if (scrapType === "API") return "АВТОМАТ";
  if (scrapType === "SELENIUM") return "АВТОМАТ";
  return "ГАР АЖИЛЛАГАА";
}

function getAccountTypeColor(type) {
  switch (type) {
    case "АВТОМАТ":
      return "green";
    case "ГАР АЖИЛЛАГАА":
      return "orange";
    default:
      return "default";
  }
}

function getScrapStatus(record) {
  const rawStatus = String(record.status || "PENDING").toUpperCase();

  switch (rawStatus) {
    case "IDLE":
      return "ХҮЛЭЭГДЭЖ БАЙНА";
    case "FAILED":
      return "АЛДААТАЙ";
    case "PENDING":
      return "SCRAP ХИЙГДЭЖ БАЙНА";
    case "STARTED":
      return "ЭХЭЛСЭН";
    case "SUCCESS":
      return "АМЖИЛТТАЙ";
    default:
      return "ХҮЛЭЭГДЭЖ БАЙНА";
  }
}

function getScrapStatusColor(status) {
  switch (status) {
    case "АЛДААТАЙ":
      return "red";
    case "ХҮЛЭЭГДЭЖ БАЙНА":
      return "orange";
    case "SCRAP ХИЙГДЭЖ БАЙНА":
      return "gold";
    case "ЭХЭЛСЭН":
      return "blue";
    case "АМЖИЛТТАЙ":
      return "green";
    default:
      return "default";
  }
}

function getScrapStatusPriority(status) {
  switch (status) {
    case "АЛДААТАЙ":
      return 1;
    case "ХҮЛЭЭГДЭЖ БАЙНА":
      return 2;
    case "SCRAP ХИЙГДЭЖ БАЙНА":
      return 3;
    case "ЭХЭЛСЭН":
      return 4;
    case "АМЖИЛТТАЙ":
      return 5;
    default:
      return 6;
  }
}

function sortAccounts(accounts) {
  return [...accounts].sort((a, b) => {
    const priorityA = getScrapStatusPriority(getScrapStatus(a));
    const priorityB = getScrapStatusPriority(getScrapStatus(b));

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    const timeA = getLastLog(a) ? new Date(getLastLog(a)).getTime() : 0;
    const timeB = getLastLog(b) ? new Date(getLastLog(b)).getTime() : 0;

    return timeB - timeA;
  });
}

function renderCountTag(count, color) {
  return <Tag color={count > 0 ? color : "default"}>{count}</Tag>;
}

function MiniStatBlock({
  label,
  value,
  bg = "#f5f5f5",
  color = "#262626",
  tooltip,
}) {
  const content = (
    <div
      style={{
        display: "inline-flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
        padding: "10px 12px",
        borderRadius: 8,
        background: bg,
        color,
        minWidth: 140,
        border: "1px solid rgba(0,0,0,0.06)",
        cursor: tooltip ? "pointer" : "default",
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.2,
          opacity: 0.9,
        }}
      >
        {label}
      </span>

      <span
        style={{
          fontWeight: 700,
          fontSize: 16,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </span>
    </div>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
}

export default function TransactionDataTablePage() {
  const navigate = useNavigate();
  const openRoute = useOpenRoute();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [searchText, setSearchText] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState();
  const [selectedAccountType, setSelectedAccountType] = useState();
  const [selectedScrapStatus, setSelectedScrapStatus] = useState();
  const [selectedRange, setSelectedRange] = useState("24");

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  async function loadAccounts() {
    try {
      setLoading(true);
      setError("");

      const data = await getScrapLogs();
      setRows(Array.isArray(data) ? data : []);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("LOAD ERROR:", err);
      setError(err?.message || "Дансны бүртгэлийг ачааллахад алдаа гарлаа");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function startScrap(range = selectedRange) {
    try {
      setLoading(true);
      setError("");

      await startScraping(Number(range));
      await loadAccounts();
    } catch (err) {
      console.error("SCRAP ERROR:", err);
      setError(err?.message || "Өгөгдөл шинэчлэхэд алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  }

  function handleUpload(record) {
    setSelectedRecord(record);
    setUploadModalOpen(true);
  }

  function handleCloseUploadModal() {
    setUploadModalOpen(false);
    setSelectedRecord(null);
  }

  async function handleImportSuccess() {
    handleCloseUploadModal();
    await loadAccounts();
  }

  useEffect(() => {
    loadAccounts();
  }, []);

  const currencyOptions = useMemo(() => {
    const values = [
      ...new Set(
        rows
          .map((item) => getCurrency(item))
          .filter(Boolean)
          .filter((v) => v !== "-")
      ),
    ];

    return values.map((value) => ({
      label: value,
      value,
    }));
  }, [rows]);

  const accountTypeOptions = [
    { label: "АВТОМАТ", value: "АВТОМАТ" },
    { label: "ГАР АЖИЛЛАГАА", value: "ГАР АЖИЛЛАГАА" },
  ];

  const scrapStatusOptions = [
    { label: "АЛДААТАЙ", value: "АЛДААТАЙ" },
    { label: "ХҮЛЭЭГДЭЖ БАЙНА", value: "ХҮЛЭЭГДЭЖ БАЙНА" },
    { label: "SCRAP ХИЙГДЭЖ БАЙНА", value: "SCRAP ХИЙГДЭЖ БАЙНА" },
    { label: "ЭХЭЛСЭН", value: "ЭХЭЛСЭН" },
    { label: "АМЖИЛТТАЙ", value: "АМЖИЛТТАЙ" },
  ];

  const rangeOptions = [
    { label: "Сүүлийн 24 цаг", value: "24" },
    { label: "Сүүлийн 7 өдөр", value: "168" },
    { label: "Сүүлийн 30 өдөр", value: "720" },
  ];

  const summaryStats = useMemo(() => {
    const institutionSet = new Set();

    let totalAccounts = 0;
    let automaticCount = 0;
    let manualCount = 0;

    rows.forEach((item) => {
      institutionSet.add(getInstitutionName(item));
      totalAccounts += 1;

      const rawType = String(getRawAccountType(item)).toUpperCase();

      if (rawType === "API" || rawType === "SELENIUM") {
        automaticCount += 1;
      } else {
        manualCount += 1;
      }
    });

    return {
      totalTradehouse: institutionSet.size,
      totalAccounts,
      automaticCount,
      manualCount,
    };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    return rows.filter((item) => {
      const accountType = getAccountType(item);
      const scrapStatus = getScrapStatus(item);
      const institutionName = getInstitutionName(item);
      const accountNumber = getAccountNumber(item);
      const bank = item.bank || "";
      const bankDisplayName = item.bankDisplayName || "";

      const matchesSearch =
        !keyword ||
        institutionName.toLowerCase().includes(keyword) ||
        String(accountNumber).toLowerCase().includes(keyword) ||
        String(bank).toLowerCase().includes(keyword) ||
        String(bankDisplayName).toLowerCase().includes(keyword);

      const matchesCurrency =
        !selectedCurrency || getCurrency(item) === selectedCurrency;
      const matchesAccountType =
        !selectedAccountType || accountType === selectedAccountType;
      const matchesScrapStatus =
        !selectedScrapStatus || scrapStatus === selectedScrapStatus;

      return (
        matchesSearch &&
        matchesCurrency &&
        matchesAccountType &&
        matchesScrapStatus
      );
    });
  }, [
    rows,
    searchText,
    selectedCurrency,
    selectedAccountType,
    selectedScrapStatus,
  ]);

  const groupedInstitutions = useMemo(() => {
    const institutionMap = new Map();

    filteredRows.forEach((account) => {
      const institutionName = getInstitutionName(account);
      const bankKey = account.bank || "NO_BANK";
      const bankLabel = getBankLabel(account);
      const institutionActive = getInstitutionActive(account);

      if (!institutionMap.has(institutionName)) {
        institutionMap.set(institutionName, {
          key: `institution-${institutionName}`,
          institutionName,
          totalAccounts: 0,
          totalBanks: 0,
          accounts: [],
          banksMap: new Map(),
          institutionActive,
        });
      }

      const institution = institutionMap.get(institutionName);
      institution.totalAccounts += 1;
      institution.accounts.push(account);

      if (!institution.banksMap.has(bankKey)) {
        institution.banksMap.set(bankKey, {
          key: `bank-${institutionName}-${bankKey}`,
          bank: bankKey,
          bankLabel,
          institutionName,
          totalAccounts: 0,
          activeAccountCount: 0,
          accounts: [],
          institutionActive,
        });
      }

      const bankGroup = institution.banksMap.get(bankKey);
      bankGroup.totalAccounts += 1;
      bankGroup.accounts.push(account);

      if (getBankAccountActive(account)) {
        bankGroup.activeAccountCount += 1;
      }
    });

    const institutions = [...institutionMap.values()].map((institution) => {
      const activeRecentCount = institution.accounts.filter((acc) =>
        isWithinRange(acc, selectedRange)
      ).length;

      const latestActivity = institution.accounts.reduce((latest, acc) => {
        const time = getLastLog(acc);
        const value = time ? new Date(time).getTime() : 0;
        return Math.max(latest, value);
      }, 0);

      const banks = [...institution.banksMap.values()]
        .map((bankGroup) => {
          const bankRecentCount = bankGroup.accounts.filter((acc) =>
            isWithinRange(acc, selectedRange)
          ).length;

          const bankLatestActivity = bankGroup.accounts.reduce((latest, acc) => {
            const time = getLastLog(acc);
            const value = time ? new Date(time).getTime() : 0;
            return Math.max(latest, value);
          }, 0);

          return {
            ...bankGroup,
            accounts: sortAccounts(bankGroup.accounts),
            activeRecentCount: bankRecentCount,
            latestActivity: bankLatestActivity,
          };
        })
        .sort((a, b) => {
          if (b.activeAccountCount !== a.activeAccountCount) {
            return b.activeAccountCount - a.activeAccountCount;
          }

          if (b.totalAccounts !== a.totalAccounts) {
            return b.totalAccounts - a.totalAccounts;
          }

          if (b.activeRecentCount !== a.activeRecentCount) {
            return b.activeRecentCount - a.activeRecentCount;
          }

          if (b.latestActivity !== a.latestActivity) {
            return b.latestActivity - a.latestActivity;
          }

          return a.bankLabel.localeCompare(b.bankLabel);
        });

      return {
        ...institution,
        totalBanks: banks.length,
        banks,
        activeRecentCount,
        latestActivity,
      };
    });

    return institutions.sort((a, b) => {
      if (Boolean(b.institutionActive) !== Boolean(a.institutionActive)) {
        return (
          Number(Boolean(b.institutionActive)) -
          Number(Boolean(a.institutionActive))
        );
      }

      if (b.activeRecentCount !== a.activeRecentCount) {
        return b.activeRecentCount - a.activeRecentCount;
      }

      if (b.latestActivity !== a.latestActivity) {
        return b.latestActivity - a.latestActivity;
      }

      if (b.totalAccounts !== a.totalAccounts) {
        return b.totalAccounts - a.totalAccounts;
      }

      return a.institutionName.localeCompare(b.institutionName);
    });
  }, [filteredRows, selectedRange]);

  const institutionColumns = [
    {
      title: "Байгууллага",
      dataIndex: "institutionName",
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: "Төлөв",
      width: 130,
      render: (_, record) => (
        <Tag color={getInstitutionStatusColor(record)}>
          {getInstitutionStatusLabel(record)}
        </Tag>
      ),
    },
    {
      title: "Сүүлийн идэвх",
      dataIndex: "activeRecentCount",
      width: 120,
      render: (v) => renderCountTag(v, "green"),
    },
    { title: "Банкны тоо", dataIndex: "totalBanks", width: 120 },
    { title: "Дансны тоо", dataIndex: "totalAccounts", width: 120 },
  ];

  const bankColumns = [
    {
      title: "Банк",
      dataIndex: "bankLabel",
      render: (v) => <strong>{v}</strong>,
    },
    {
      title: "Төлөв",
      width: 160,
      render: (_, record) => (
        <Tag color={getBankGroupStatusColor(record)}>
          {getBankGroupStatusLabel(record)}
        </Tag>
      ),
    },
    {
      title: "Сүүлийн идэвх",
      dataIndex: "activeRecentCount",
      width: 120,
      render: (v) => renderCountTag(v, "green"),
    },
    { title: "Данс", dataIndex: "totalAccounts", width: 100 },
  ];

  const accountColumns = [
    {
      title: "Дансны дугаар",
      width: 220,
      render: (_, record) => {
        const accountNumber = getAccountNumber(record);
        const bankAccountId = getBankAccountId(record);

        if (!bankAccountId) {
          return accountNumber;
        }

        return (
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() => navigate(`/account/${bankAccountId}`)}
          >
            {accountNumber}
          </Button>
        );
      },
    },
    {
      title: "Төлөв",
      width: 130,
      render: (_, record) => (
        <Tag color={getAccountStatusColor(record)}>
          {getAccountStatusLabel(record)}
        </Tag>
      ),
    },
    {
      title: "Scrap төлөв",
      width: 180,
      render: (_, record) => {
        const status = getScrapStatus(record);
        return <Tag color={getScrapStatusColor(status)}>{status}</Tag>;
      },
    },
    {
      title: "Валют",
      width: 110,
      render: (_, record) => {
        const v = getCurrency(record);
        return v && v !== "-" ? <Tag color="blue">{v}</Tag> : "-";
      },
    },
    {
      title: "Дансны төрөл",
      width: 160,
      render: (_, record) => {
        const type = getAccountType(record);
        return <Tag color={getAccountTypeColor(type)}>{type}</Tag>;
      },
    },
    {
      title: "Сүүлийн бүртгэл",
      width: 220,
      render: (_, record) => {
        const time = getLastLog(record);
        if (!time) return "-";

        const inRange = isWithinRange(record, selectedRange);

        return (
          <Tag color={inRange ? "green" : "default"}>
            {formatDateTime(time)}
          </Tag>
        );
      },
    },
    {
      title: "Үйлдэл",
      width: 140,
      align: "right",
      render: (_, record) => {
        const scrapStatus = getScrapStatus(record);

        return (
          <Button
            size="small"
            type={scrapStatus === "АЛДААТАЙ" ? "primary" : "default"}
            danger={scrapStatus === "АЛДААТАЙ"}
            icon={<UploadOutlined />}
            onClick={() => handleUpload(record)}
          >
            Оруулах
          </Button>
        );
      },
    },
  ];

  function handleReset() {
    setSearchText("");
    setSelectedCurrency(undefined);
    setSelectedAccountType(undefined);
    setSelectedScrapStatus(undefined);
  }

  const selectedBankValue = getSelectedBankValue(selectedRecord);
  const selectedAccountNumber = getSelectedAccountNumber(selectedRecord);

  const handleSearchStatements = (event) => {
    openRoute(`/search-statements`, event);
  };

  return (
    <>
      <Card>
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Бүх дансны сүүлийн бүртгэл
              </Title>
              <Text type="secondary">Байгууллага → Банк → Данс ангиллаар</Text>
            </div>
            <Button
              icon={<SearchOutlined />}
              className="
                h-11 rounded-[18px] border border-slate-200
                bg-slate-50 text-slate-700 shadow-none
                hover:!border-slate-300 hover:!bg-white hover:!text-slate-900
              "
              onClick={(e) => handleSearchStatements?.(e)}
            >
              Гүйлгээ хайх
            </Button>
          </div>

          {error && (
            <Alert
              type="error"
              message="Өгөгдөл ачааллахад алдаа гарлаа"
              description={error}
              showIcon
            />
          )}

          <Card
            size="small"
            style={{
              background: "#fafafa",
              border: "1px solid #f0f0f0",
            }}
          >
            <Space size={[10, 10]} wrap>
              <MiniStatBlock
                label="Байгууллага"
                value={summaryStats.totalTradehouse}
                bg="#e6f4ff"
                color="#0958d9"
              />
              <MiniStatBlock
                label="Данс"
                value={summaryStats.totalAccounts}
                bg="#f9f0ff"
                color="#531dab"
              />
              <MiniStatBlock
                label="Автомат"
                value={summaryStats.automaticCount}
                bg="#f0f5ff"
                color="#1d39c4"
              />
              <MiniStatBlock
                label="Гар ажиллагаа"
                value={summaryStats.manualCount}
                bg="#fff2e8"
                color="#d4380d"
              />
            </Space>
          </Card>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <Space wrap size="middle">
              <Input
                style={{ width: 260 }}
                placeholder="Хайх..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />

              <Select
                style={{ width: 140 }}
                placeholder="Валют"
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                allowClear
                options={currencyOptions}
              />

              <Select
                style={{ width: 180 }}
                placeholder="Дансны төрөл"
                value={selectedAccountType}
                onChange={setSelectedAccountType}
                allowClear
                options={accountTypeOptions}
              />

              <Select
                style={{ width: 180 }}
                placeholder="Scrap төлөв"
                value={selectedScrapStatus}
                onChange={setSelectedScrapStatus}
                allowClear
                options={scrapStatusOptions}
              />

              <Button onClick={handleReset}>Цэвэрлэх</Button>
            </Space>

            <Space wrap size="middle" align="center">
              <Select
                style={{ width: 160 }}
                value={selectedRange}
                onChange={setSelectedRange}
                options={rangeOptions}
              />

              <Button
                type="primary"
                loading={loading}
                onClick={() => startScrap(selectedRange)}
              >
                Өгөгдөл шинэчлэх
              </Button>

              <Text type="secondary">
                Сүүлд шинэчилсэн: {formatDateTime(lastUpdated)}
              </Text>
            </Space>
          </div>

          <Table
            rowKey="key"
            loading={loading}
            columns={institutionColumns}
            dataSource={groupedInstitutions}
            pagination={false}
            locale={{
              emptyText: error ? "Өгөгдөл байхгүй" : "Мэдээлэл алга",
            }}
            expandable={{
              expandedRowRender: (institution) => (
                <Table
                  rowKey="key"
                  columns={bankColumns}
                  dataSource={institution.banks}
                  pagination={false}
                  size="small"
                  locale={{
                    emptyText: "Банкны мэдээлэл алга",
                  }}
                  expandable={{
                    expandedRowRender: (bank) => (
                      <Table
                        rowKey={(r) =>
                          r.bankAccountId || r.id || r.accountNumber
                        }
                        columns={accountColumns}
                        dataSource={bank.accounts}
                        pagination={false}
                        size="small"
                        scroll={{ x: 1400 }}
                        locale={{
                          emptyText: "Дансны мэдээлэл алга",
                        }}
                      />
                    ),
                  }}
                />
              ),
            }}
          />
        </Space>
      </Card>

      <Modal
        title="Excel файл оруулах"
        open={uploadModalOpen}
        onCancel={handleCloseUploadModal}
        footer={null}
        width={820}
        destroyOnClose
      >
        {selectedRecord && (
          <div style={{ marginBottom: 16 }}>
            <Alert
              type="info"
              showIcon
              message="Сонгосон данс"
              description={
                <div>
                  <div>
                    <strong>Байгууллага:</strong>{" "}
                    {getInstitutionName(selectedRecord)}
                  </div>
                  <div>
                    <strong>Төлөв:</strong>{" "}
                    {getAccountStatusLabel(selectedRecord)}
                  </div>
                  <div>
                    <strong>Банк:</strong> {getBankLabel(selectedRecord)}
                  </div>
                  <div>
                    <strong>Банк код:</strong> {selectedBankValue || "-"}
                  </div>
                  <div>
                    <strong>Дансны дугаар:</strong>{" "}
                    {selectedAccountNumber || "-"}
                  </div>
                  <div>
                    <strong>Scrap төлөв:</strong>{" "}
                    {getScrapStatus(selectedRecord)}
                  </div>
                </div>
              }
            />
          </div>
        )}

        <UploadExcelAcc
          selectedBank={selectedBankValue}
          selectedAccountNumber={selectedAccountNumber}
          onImportSuccess={handleImportSuccess}
          extraAction={<Button onClick={handleCloseUploadModal}>Хаах</Button>}
        />
      </Modal>
    </>
  );
}